import React, { useContext, useState, useEffect, useMemo } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import ValuePoint from 'admin/components/ValuePoint'
import { Card, Grid, Typography, Box, Collapse, Button } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { differenceInYears, differenceInMonths, addMonths } from 'date-fns'
import { useSocialSecurityCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useSocialSecurityCalculations'
import { DataGrid } from '@mui/x-data-grid'

const dateToYears = (startDate, endDate) => {
  const years =
    startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  const diff =
    startDate && endDate
      ? differenceInMonths(new Date(endDate), new Date(startDate))
      : ''
  const months =
    startDate && endDate
      ? differenceInMonths(
          new Date(endDate),
          addMonths(new Date(startDate), years * 12)
        )
      : ''
  return { years, months }
}

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function SocialSecurity({
  toggleCollapse,
  collapseSections,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const [localForm, setLocalForm] = useState({
    estimatedSocialSecurity: intakeState.estimatedSocialSecurity || '',
    socialSecurityFinderAmount: intakeState.socialSecurityFinderAmount || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const currentlyCollectingSS = intakeState?.alreadyCollectingSocialSecurity
  const { generateSSARows, calculateSSBenefit, getFRA } =
    useSocialSecurityCalculations()

  const clientBirthYear = new Date(
    intakeState?.dateOfBirth || new Date()
  ).getFullYear()
  const clientFRA = getFRA(clientBirthYear || 1960)

  // Finder Table
  const columns = [
    { field: 'age', headerName: 'Age', flex: 1 },
    { field: 'income', headerName: 'Income', flex: 1 },
  ]
  const rows = generateSSARows(
    intakeState?.socialSecurityFinderAge || 65,
    intakeState?.socialSecurityFinderAmount || 2400
  )

  // Model Table
  const modelColumns = [
    { field: 'age', headerName: 'Social Security Start Age', flex: 1 },
    { field: 'income', headerName: 'Income', flex: 1 },
  ]

  const modelRows = useMemo(() => {
    return Array.from({ length: 70 - 62 + 1 }, (_, i) => {
      const age = 62 + i

      const income = calculateSSBenefit({
        fraAge: clientFRA || 67,
        baseIncome: intakeState?.socialSecurityModeledAmount,
        baseType: intakeState?.socialSecurityModel || 'fullRetirementAge',
        claimAgeYears: age,
        claimAgeMonths: 0,
        birthYear: clientBirthYear,
      })

      return {
        id: age,
        age: `Age ${age}`,
        income: `$${
          income?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) || 0
        }`,
      }
    })
  }, [clientFRA, intakeState])

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.socialSecurity}
                style={{ width: '100%' }}
              >
                <ValuePoint text="If Unsure about SS Start, Discuss Pros & Cons" />
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="socialsecurity"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('socialSecurity')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Social Security
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.socialSecurity
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse
            in={collapseSections.socialSecurity}
            style={{ width: '100%' }}
          >
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'alreadyCollectingSocialSecurity',
                      label: 'Are you currently collecting Social Security?',
                      placeholder:
                        'Are you currently collecting Social Security?',
                      inputType: 'select',
                      value: intakeState.alreadyCollectingSocialSecurity,
                      onChange: updateIntakeStateAndSubmit(
                        (alreadyCollectingSocialSecurity) =>
                          updateIntakeStateAtKey({
                            alreadyCollectingSocialSecurity,
                          })
                      ),
                      options: selectData.yesNo,
                    })}
                  </Grid>
                  {currentlyCollectingSS ? (
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'estimatedSocialSecurity',
                        label: 'What is your Monthly Social Security benefit?',
                        placeholder: '$2,400.00',
                        inputType: 'currencyFloat',
                        value: localForm.estimatedSocialSecurity,
                        onChange: (e) =>
                          handleChange('estimatedSocialSecurity')(e),
                        onBlur: updateIntakeStateAndSubmit(
                          (estimatedSocialSecurity) =>
                            updateIntakeStateAtKey({ estimatedSocialSecurity })
                        ),
                      })}
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'estimatedSocialSecurity',
                        label:
                          'Estimated Social Security benefit at Full Retirement Age (FRA)',
                        placeholder: '$2,400.00',
                        inputType: 'currencyFloat',
                        value: localForm.estimatedSocialSecurity,
                        onChange: (e) =>
                          handleChange('estimatedSocialSecurity')(e),
                        onBlur: updateIntakeStateAndSubmit(
                          (estimatedSocialSecurity) =>
                            updateIntakeStateAtKey({ estimatedSocialSecurity })
                        ),
                      })}
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    background: '#efefef',
                    paddingRight: '16px',
                    paddingBottom: '16px',
                  }}
                  mt={0}
                >
                  <Grid item xs={12}>
                    <Typography variant="h5">
                      In Progress Caclulations Change
                    </Typography>
                    <Typography variant="body2">
                      This takes into account which model you choose to use, the
                      amount at that model and your SS start age and implements
                      it on the income gap and dashboard table. If you do not
                      use the model this will defualt back to the FRA model
                      above.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'socialSecurityModel',
                      label: 'Social Security Model',
                      placeholder: 'Social Security Model',
                      inputType: 'select',
                      value: intakeState.socialSecurityModel,
                      onChange: updateIntakeStateAndSubmit(
                        (socialSecurityModel) =>
                          updateIntakeStateAtKey({
                            socialSecurityModel,
                          })
                      ),
                      options: [
                        {
                          value: 'fullRetirementAge',
                          label: 'Full Retirement Age',
                        },
                        { value: 'amountAt62', label: 'Amount at Age 62' },
                      ],
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'socialSecurityModeledAmount',
                      label: 'Social Security Model Amount',
                      placeholder: '$2,400.00',
                      inputType: 'currencyFloat',
                      value: intakeState.socialSecurityModeledAmount,
                      onChange: (e) =>
                        handleChange('socialSecurityModeledAmount')(e),
                      onBlur: updateIntakeStateAndSubmit(
                        (socialSecurityModeledAmount) =>
                          updateIntakeStateAtKey({
                            socialSecurityModeledAmount,
                          })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        autoFocus: false,
                        disabled: true,
                        id: 'age',
                        label: 'Retirement Age (Years)',
                        inputType: 'text',
                        value: dateToYears(
                          intakeState.dateOfBirth,
                          intakeState.dateOfPlannedRetirement
                        ).years,
                      })}
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        autoFocus: false,
                        disabled: true,
                        id: 'age',
                        label: 'Retirement Age (Months)',
                        inputType: 'text',
                        value:
                          dateToYears(
                            intakeState.dateOfBirth,
                            intakeState.dateOfPlannedRetirement
                          ).months || '0',
                      })}
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        autoFocus: false,
                        disabled: true,
                        id: 'fra',
                        label: 'Client FRA',
                        inputType: 'text',
                        value: clientFRA || 67,
                      })}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'ageToBeginCollectingSS',
                      label: 'SS Start Age (Years)',
                      placeholder: 'SS Start Age Years',
                      inputType: 'select',
                      value: intakeState.ageToBeginCollectingSS,
                      onChange: updateIntakeStateAndSubmit(
                        (ageToBeginCollectingSS) =>
                          updateIntakeStateAtKey({
                            ageToBeginCollectingSS,
                          })
                      ),
                      options: selectData.retirementSSYear,
                    })}
                  </Grid>
                  <Grid item xs={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'ageToBeginCollectingSSMonths',
                      label: 'SS Start Age (Months)',
                      placeholder: 'SS Start Age Months',
                      inputType: 'select',
                      value: intakeState.ageToBeginCollectingSSMonths,
                      onChange: updateIntakeStateAndSubmit(
                        (ageToBeginCollectingSSMonths) =>
                          updateIntakeStateAtKey({
                            ageToBeginCollectingSSMonths,
                          })
                      ),
                      options: selectData.retirementSSMonth,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" mb={1}>
                      Modeled Estimate for Gross Monthly Social Security @ SS
                      Start Age:{' '}
                      {currentlyCollectingSS
                        ? dollarCentsFormat.format(
                            intakeState?.estimatedSocialSecurity || 0
                          )
                        : dollarCentsFormat.format(
                            calculateSSBenefit({
                              fraAge: clientFRA || 67,
                              baseIncome:
                                intakeState?.socialSecurityModeledAmount,
                              baseType:
                                intakeState?.socialSecurityModel ||
                                'fullRetirementAge',
                              claimAgeYears:
                                intakeState?.ageToBeginCollectingSS || 67,
                              claimAgeMonths:
                                intakeState?.ageToBeginCollectingSSMonths || 0,
                              birthYear: clientBirthYear,
                            }) || 0
                          )}
                    </Typography>
                    {(intakeState?.socialSecurityModel ||
                      intakeState?.socialSecurityModeledAmount ||
                      intakeState?.ageToBeginCollectingSS ||
                      intakeState?.ageToBeginCollectingSSMonths) && (
                      <Button
                        variant="parcoRounded"
                        size="small"
                        color="secondary"
                        onClick={() =>
                          updateIntakeStateAndSubmit(() =>
                            updateIntakeStateAtKey({
                              ageToBeginCollectingSS: null,
                              ageToBeginCollectingSSMonths: null,
                              socialSecurityModeledAmount: null,
                              socialSecurityModel: null,
                            })
                          )()
                        }
                      >
                        Clear Model and Use Standard
                      </Button>
                    )}
                  </Grid>
                  {intakeState.socialSecurityModel &&
                    intakeState.socialSecurityModeledAmount && (
                      <Grid item xs={12}>
                        <DataGrid
                          autoHeight
                          rows={modelRows}
                          columns={modelColumns}
                          hideFooter
                          disableColumnMenu
                          disableColumnSelector
                          density="compact"
                          sx={{
                            '& .MuiDataGrid-cell:last-of-type': {
                              borderLeft: '1px solid #e0e0e0',
                            },
                            '& .MuiDataGrid-cell:focus': {
                              outline: 'none',
                            },
                          }}
                        />
                      </Grid>
                    )}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5">SSA Finder (Basic)</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'socialSecurityFinderAge',
                      label: 'SS Finder Age',
                      placeholder: 'SS Finder Age',
                      inputType: 'select',
                      value: intakeState.socialSecurityFinderAge,
                      onChange: updateIntakeStateAndSubmit(
                        (socialSecurityFinderAge) =>
                          updateIntakeStateAtKey({
                            socialSecurityFinderAge,
                          })
                      ),
                      options: selectData.retirementSSYear,
                    })}
                  </Grid>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'socialSecurityFinderAmount',
                      label: 'Social Security Finder Amount',
                      placeholder: '$2,400.00',
                      inputType: 'currencyFloat',
                      value: intakeState.socialSecurityFinderAmount,
                      onChange: (e) =>
                        handleChange('socialSecurityFinderAmount')(e),
                      onBlur: updateIntakeStateAndSubmit(
                        (socialSecurityFinderAmount) =>
                          updateIntakeStateAtKey({ socialSecurityFinderAmount })
                      ),
                    })}
                  </Grid>
                  {intakeState.socialSecurityFinderAge &&
                  intakeState.socialSecurityFinderAmount ? (
                    <Grid item xs={12}>
                      <Typography variant="h5">
                        Calculated Social Security
                      </Typography>
                      <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        hideFooter
                        disableColumnMenu
                        disableColumnSelector
                        density="compact"
                        sx={{
                          '& .MuiDataGrid-cell:last-of-type': {
                            borderLeft: '1px solid #e0e0e0',
                          },
                          '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                          },
                        }}
                      />
                    </Grid>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default SocialSecurity
